import React, { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Search, Clock, AlertTriangle, CheckCircle2, RefreshCw, Users, Package, ChevronDown, X } from "lucide-react";
import { useApiData } from "../../lib/api.js";
import { PlovouciNabidka } from "../../components/ui.jsx";

// 28.5.2026: Klik na klienta otevře plný ClientDetail v restrict módu (jako v Clients
// Management). Lazy load - modal se načte až když je potřeba (úspora bundle).
const ClientDetail = lazy(() => import("../ClientDetail.jsx"));

// Filtry seznamu klientek. Pořadí je i pořadí v rozbalovacím výběru.
const FILTRY = [
  { klic: "aktivni", popis: "Aktivní" },
  { klic: "konci", popis: "Končí do 30 dní" },
  { klic: "neaktivni", popis: "Neaktivní 14 a více dní" },
  { klic: "vse", popis: "Všichni" },
];

// === TRENER KLIENTI - list klientů přiřazených aktuálnímu trenérovi ===
// Datový zdroj: /api/clients (Stav 2026 sheet) → filter per Trenér == activeTrener.
// Filtry: aktivní / končící do 30 dní / neaktivní 14+ dní / všichni
// Sort: jméno / poslední trénink / konec spolupráce
// Klik na řádek → rozbalí detail (kontakt, balíček, fakturace, tréninky)

function todayIso() {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDaysIso(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function daysBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return null;
  const ms = new Date(toIso + "T00:00:00").getTime() - new Date(fromIso + "T00:00:00").getTime();
  return Math.round(ms / 86400000);
}

export default function TrenerKlienti() {
  const { activeTrener, isOwner } = useOutletContext() || {};
  const { data, loading, error, refetch, refreshing } = useApiData("/api/clients");
  const [filter, setFilter] = useState("aktivni"); // aktivni | konci | neaktivni | vse
  // Pre-fill search z URL ?q=jmeno (proklik z týdenního rozvrhu) + filter "vse" aby ukázalo i ukončené
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get("q") || "";
  const [search, setSearch] = useState(initialSearch);
  // Pokud přijdeme s ?q=, přepneme i filter na "vse" aby se ukázal i klient se stavem Ukončeno
  useEffect(() => {
    if (initialSearch) setFilter("vse");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearch]);
  const [sortBy, setSortBy] = useState("jmeno");
  const [selected, setSelected] = useState(null); // klient otevřený v ClientDetail modalu
  // Toggle pro zobrazení i cizích klientů (např. když trenér zaskakuje za jiného).
  // 29.5.2026 v7: default TRUE — trenér často zaskakuje a potřebuje cizí klienty najít hned
  // (např. potřebuje jim něco napsat). Vlastní klienty má vždy nahoře v listu.
  const [filtrOtevren, setFiltrOtevren] = useState(false);
  const filtrRef = useRef(null);
  const [showCizi, setShowCizi] = useState(true);

  const today = todayIso();
  const in30Days = addDaysIso(today, 30);
  const ago14days = addDaysIso(today, -14);

  const allClients = data?.clients || [];

  // Proklik z detailu úkolu: ?open=<jméno klienta> → otevři rovnou ClientDetail modal.
  const openName = searchParams.get("open") || "";
  useEffect(() => {
    if (!openName || selected) return;
    const c = allClients.find((x) => String(x.klient || x.klient_name || "").toLowerCase().trim() === openName.toLowerCase().trim());
    if (c) { setSelected(c); setFilter("vse"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openName, allClients]);

  // Filtr per pilot - případně rozšířený o cizí klienty (search nebo toggle)
  const myClients = useMemo(() => {
    if (!activeTrener) return [];
    const pilotLower = activeTrener.toLowerCase().trim();
    // Pokud user hledá konkrétní jméno NEBO ručně zapnul "Zobrazit i cizí" → vrátit všechny
    // s flagem _isCizi (true pokud klient má jiného oficiálního pilota)
    if (search.trim() || showCizi) {
      return allClients.map((c) => ({
        ...c,
        _isCizi: (c.trener || "").toLowerCase().trim() !== pilotLower,
      }));
    }
    return allClients
      .filter((c) => (c.trener || "").toLowerCase().trim() === pilotLower)
      .map((c) => ({ ...c, _isCizi: false }));
  }, [allClients, activeTrener, search, showCizi]);

  // Doplníme každému klientovi spočítané flagy
  const enriched = useMemo(() => {
    return myClients.map((c) => {
      const isActive = c.stav === "Aktivní";
      const endsSoon = isActive && c.konec && c.konec >= today && c.konec <= in30Days;
      const isInactive = isActive && c.posledniTrenink && c.posledniTrenink < ago14days;
      const daysToEnd = c.konec ? daysBetween(today, c.konec) : null;
      const daysFromLast = c.posledniTrenink ? daysBetween(c.posledniTrenink, today) : null;
      return { ...c, _isActive: isActive, _endsSoon: endsSoon, _isInactive: isInactive, _daysToEnd: daysToEnd, _daysFromLast: daysFromLast };
    });
  }, [myClients, today, in30Days, ago14days]);

  // Aplikace filtru + searche
  const filtered = useMemo(() => {
    let f = enriched;
    if (filter === "aktivni") f = f.filter((c) => c._isActive);
    if (filter === "konci") f = f.filter((c) => c._endsSoon);
    if (filter === "neaktivni") f = f.filter((c) => c._isInactive);
    // "vse" → bez filtru

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      f = f.filter((c) => (c.klient || "").toLowerCase().includes(q));
    }

    // Sort - cizí klienty (_isCizi) vždy řadit nakonec, ať trenér vidí své první.
    // Až poté řadit podle sortBy uvnitř každé skupiny (vlastní / cizí).
    const sorted = [...f].sort((a, b) => {
      const aCizi = a._isCizi ? 1 : 0;
      const bCizi = b._isCizi ? 1 : 0;
      if (aCizi !== bCizi) return aCizi - bCizi; // vlastní (0) před cizí (1)
      if (sortBy === "konec") {
        if (!a.konec && !b.konec) return (a.klient || "").localeCompare(b.klient || "", "cs");
        if (!a.konec) return 1;
        if (!b.konec) return -1;
        return a.konec.localeCompare(b.konec);
      }
      if (sortBy === "posledniTrenink") {
        if (!a.posledniTrenink && !b.posledniTrenink) return 0;
        if (!a.posledniTrenink) return 1;
        if (!b.posledniTrenink) return -1;
        return a.posledniTrenink.localeCompare(b.posledniTrenink);
      }
      return (a.klient || "").localeCompare(b.klient || "", "cs");
    });
    return sorted;
  }, [enriched, filter, search, sortBy]);

  const counts = useMemo(() => {
    // Counts vždy z VLASTNÍCH klientů (pilot match), ne s cizími - chips počítají jen tvoje
    const own = enriched.filter((c) => !c._isCizi);
    return {
      aktivni: own.filter((c) => c._isActive).length,
      konci: own.filter((c) => c._endsSoon).length,
      neaktivni: own.filter((c) => c._isInactive).length,
      vse: own.length,
      cizi: enriched.filter((c) => c._isCizi).length,
    };
  }, [enriched]);

  if (isOwner && !activeTrener) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
        <Users className="w-10 h-10 text-brand-300 mx-auto mb-2" />
        <div className="text-base font-bold text-slate-700 mb-1">Vyber trenéra</div>
        <div className="text-sm text-slate-500">V hlavičce nahoře vyber kterého trenéra chceš vidět.</div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* === HLAVIČKA === */}
      <div className="rounded-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 p-4 text-white shadow-glass sm:p-5">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 sm:h-11 sm:w-11">
            <Users className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-serif text-xl font-bold leading-tight sm:text-2xl">Klienti</div>
            <p className="mt-1 text-xs leading-snug text-white/75 sm:text-sm">{counts.aktivni} aktivních z celkem {counts.vse}</p>
          </div>
          <button
            onClick={refetch}
            disabled={refreshing}
            aria-label="Obnovit"
            title="Obnovit"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition active:scale-95 active:bg-white/20 disabled:opacity-40 sm:h-10 sm:w-10 sm:hover:bg-white/20"
          >
            <RefreshCw className={`h-[18px] w-[18px] ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* === HLEDÁNÍ A FILTR === */}
      {/* Rozhodnutí z fáze 0: filtr je rozbalovací výběr jako portfolio v PM,
          nikdy řada čipů rolující do strany. */}
      <div className="glass min-w-0 p-4 sm:p-5">
        <div className="relative mb-2.5">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hledat klientku"
            className="field !pl-9"
          />
        </div>

        <div ref={filtrRef} className="relative mb-2.5">
          <button
            type="button"
            onClick={() => setFiltrOtevren((o) => !o)}
            aria-expanded={filtrOtevren}
            className="flex min-h-[60px] w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 text-left shadow-sm"
          >
            <span className="shrink-0 rounded-full bg-brand-light px-3 py-1.5 text-sm font-semibold text-brand-deep">Filtr</span>
            <span className="min-w-0 flex-1 text-base font-bold text-slate-900">{FILTRY.find((f) => f.klic === filter)?.popis || "Aktivní"}</span>
            <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${filtrOtevren ? "rotate-180" : ""}`} />
          </button>
          <PlovouciNabidka kotva={filtrRef} otevreno={filtrOtevren} onZavrit={() => setFiltrOtevren(false)} role="listbox" className="p-1.5 shadow-glass" maxVyska={420}>
              {FILTRY.map((f) => {
                const vybrano = filter === f.klic;
                return (
                  <button
                    key={f.klic}
                    role="option"
                    aria-selected={vybrano}
                    onClick={() => { setFilter(f.klic); setFiltrOtevren(false); }}
                    className={`flex min-h-[48px] w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-sm ${vybrano ? "bg-brand-light font-semibold text-brand-deep" : "text-slate-700"}`}
                  >
                    {vybrano ? <CheckCircle2 className="h-4 w-4 shrink-0 text-brand" /> : <span className="h-4 w-4 shrink-0" />}
                    <span className="min-w-0 flex-1">{f.popis}</span>
                    <span className={`shrink-0 rounded-lg px-1.5 py-0.5 text-xs font-bold tabular-nums ${vybrano ? "bg-white text-brand-deep" : "bg-slate-100 text-slate-600"}`}>
                      {counts[f.klic]}
                    </span>
                  </button>
                );
              })}
            </PlovouciNabidka>
        </div>

        <label className="flex min-h-[44px] items-center gap-2.5 text-sm text-slate-700" title="Zobrazí i klientky, které mají oficiálního coache jiného trenéra. Užitečné při zaskakování.">
          <input
            type="checkbox"
            checked={showCizi}
            onChange={(e) => setShowCizi(e.target.checked)}
            className="h-5 w-5 shrink-0 rounded border-slate-300 accent-brand"
          />
          Zobrazit i cizí klientky, u kterých zaskakuju
        </label>

        <div className="mt-2.5">
          <label className="label" htmlFor="razeni-klientu">Řadit</label>
          <select
            id="razeni-klientu"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="field"
          >
            <option value="jmeno">Podle jména</option>
            <option value="konec">Podle konce spolupráce</option>
            <option value="posledniTrenink">Podle posledního tréninku</option>
          </select>
        </div>
      </div>

      {/* === STAVY === */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">Chyba: {error}</div>
      )}
      {loading && (
        <div className="flex items-center justify-center py-10 text-sm text-slate-400">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Načítám…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl py-10 px-4 text-center">
          <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <div className="text-sm text-slate-500">
            {myClients.length === 0
              ? activeTrener ? `Pro trenéra ${activeTrener} nejsou žádní klienti.` : "Vyber trenéra v hlavičce."
              : "Žádný klient neodpovídá filtru."}
          </div>
        </div>
      )}

      {/* === LIST KARET - klik otevře ClientDetail modal (restrictMode="trener") === */}
      <div className="space-y-2">
        {filtered.map((c) => (
          <ClientCard
            key={`${c.klient}_${c.zacatek || ""}`}
            client={c}
            onOpen={() => setSelected(c)}
          />
        ))}
      </div>

      {/* ClientDetail modal - restrict mode skryje Komentáře, Email, Spolupráce,
          Finance, Historie plateb, Vstupní dotazník + Notion link v Kontaktu. */}
      {selected && (
        <Suspense fallback={null}>
          <ClientDetail
            client={selected}
            onClose={() => setSelected(null)}
            restrictMode="trener"
          />
        </Suspense>
      )}
    </div>
  );
}


function ClientCard({ client: c, onOpen }) {
  // 28.5.2026: inline expand zrušen. Klik = otevřít plný ClientDetail (restrictMode).
  let statusBadge = null;
  if (c._endsSoon) {
    statusBadge = { text: c._daysToEnd === 0 ? "Končí dnes" : `Končí za ${c._daysToEnd} dní`, bg: "bg-red-100 text-red-800 border-red-200", icon: AlertTriangle };
  } else if (c._isInactive) {
    statusBadge = { text: `${c._daysFromLast} dní bez tréninku`, bg: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock };
  } else if (c._isActive) {
    statusBadge = { text: "Aktivní", bg: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 };
  } else if (c.stav === "Ukončeno") {
    statusBadge = { text: "Ukončeno", bg: "bg-slate-100 text-slate-600 border-slate-200", icon: X };
  } else if (c.stav) {
    statusBadge = { text: c.stav, bg: "bg-slate-100 text-slate-700 border-slate-200", icon: null };
  }

  const StatusIcon = statusBadge?.icon;
  const accentBorder = c._endsSoon ? "border-l-4 border-l-red-500"
                     : c._isInactive ? "border-l-4 border-l-amber-500"
                     : c._isActive ? "border-l-4 border-l-emerald-500"
                     : "border-l-4 border-l-slate-300";

  const lastTrainingLabel = c.posledniTrenink
    ? c._daysFromLast === 0 ? "dnes" : c._daysFromLast === 1 ? "včera" : `před ${c._daysFromLast} dny`
    : "nikdy";

  // === Pill boxy s důležitými daty na první pohled ===
  // Pravidla balíčků (sjednoceno s klientským portálem public/index.html getPackageRules):
  //   Permanentka → monthlyAdded 10, monthlyMin 2
  //   3T → monthlyAdded 12, monthlyMin 9
  //   2T → monthlyAdded 8, monthlyMin 6
  //   1T (default) → monthlyAdded 4, monthlyMin 3
  // 3T musí být checked PŘED 2T (jinak "3t" zachytí .includes("2t") = false ale ".includes('3t')" musí být první).
  const packageRules = (() => {
    const p = String(c.balicek || "").toLowerCase();
    if (p.includes("permanentka") || p.includes("permice")) return { label: "Permanentka", monthlyAdded: 10, monthlyMin: 2 };
    if (p.includes("3t")) return { label: "3T", monthlyAdded: 12, monthlyMin: 9 };
    if (p.includes("2t")) return { label: "2T", monthlyAdded: 8, monthlyMin: 6 };
    return { label: "1T", monthlyAdded: 4, monthlyMin: 3 };
  })();

  // Tréninky k odchození - žádný sub status, jen barva podle stavu.
  const stavTreninkuNum = parseInt(c.stavTreninku, 10);
  const stavTreninkuPill = !isNaN(stavTreninkuNum) ? (() => {
    let bg = "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (stavTreninkuNum < 0) bg = "bg-brand-50 text-brand-700 border-brand-200";
    else if (stavTreninkuNum === 0) bg = "bg-rose-50 text-rose-700 border-rose-200";
    else if (stavTreninkuNum < 3) bg = "bg-amber-50 text-amber-700 border-amber-200";
    return { value: stavTreninkuNum, bg };
  })() : null;

  // Tento měsíc X/Y - done out of monthly target. Barva podle vztahu k minimu.
  const tentoMesicDone = parseInt(c.treninkyTentoMesic ?? 0, 10) || 0;
  const tentoMesicPill = (() => {
    const target = packageRules.monthlyAdded;
    const min = packageRules.monthlyMin;
    let bg = "bg-brand-50 text-brand-700 border-brand-200";
    if (tentoMesicDone < min - 1) bg = "bg-rose-50 text-rose-700 border-rose-200";
    else if (tentoMesicDone < min) bg = "bg-amber-50 text-amber-700 border-amber-200";
    else if (tentoMesicDone >= min) bg = "bg-emerald-50 text-emerald-700 border-emerald-200";
    return { value: `${tentoMesicDone}/${target}`, bg };
  })();

  // Poslední faktura - label "Poslední faktura" + status (Zaplaceno / Vystavená / Po splatnosti Xd).
  const fakturaPill = (() => {
    const st = c.fapiLastInvoiceStatus;
    if (st === "overdue") return { value: `Po splatnosti ${c.fapiLastInvoiceDaysOverdue || "?"} d`, bg: "bg-rose-50 text-rose-700 border-rose-200" };
    if (st === "pending") return { value: "Vystavená", bg: "bg-blue-50 text-blue-700 border-blue-200" };
    if (st === "paid") {
      if (c.fapiLastInvoiceDaysOverdue > 0) return { value: `Zaplaceno (${c.fapiLastInvoiceDaysOverdue} d po)`, bg: "bg-rose-50 text-rose-700 border-rose-200" };
      return { value: "Zaplaceno", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    }
    if (!st && c.lastCashPaymentDate) return { value: "Zaplaceno hotově", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    return { value: "-", bg: "bg-slate-50 text-slate-600 border-slate-200" };
  })();

  // Celková délka spolupráce - od zacatek do dneška (nebo konec spolupráce pokud existuje).
  const spolupracePill = (() => {
    if (!c.zacatek) return null;
    const from = new Date(c.zacatek + "T00:00:00");
    const to = c.konec ? new Date(c.konec + "T00:00:00") : new Date();
    let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
    if (to.getDate() < from.getDate()) months--;
    if (months < 1) return { value: "< 1 měsíc", bg: "bg-slate-50 text-slate-700 border-slate-200" };
    if (months < 12) return { value: `${months} ${months < 5 ? "měsíce" : "měsíců"}`, bg: "bg-slate-50 text-slate-700 border-slate-200" };
    const years = Math.floor(months / 12);
    const remMonths = months - years * 12;
    const ylabel = years === 1 ? "rok" : years < 5 ? "roky" : "let";
    const value = remMonths === 0 ? `${years} ${ylabel}` : `${years} ${ylabel} ${remMonths} m`;
    return { value, bg: "bg-slate-50 text-slate-700 border-slate-200" };
  })();

  // Přihlášení v app - lastPortalLoginAt (jen reálné SMS verify, ne admin impersonate).
  const appLoginPill = (() => {
    if (!c.lastPortalLoginAt) return { value: "Nikdy", bg: "bg-slate-100 text-slate-700 border-slate-200" };
    const last = new Date(c.lastPortalLoginAt);
    if (isNaN(last.getTime())) return { value: "Nikdy", bg: "bg-slate-100 text-slate-700 border-slate-200" };
    const daysAgo = Math.floor((Date.now() - last.getTime()) / 86400000);
    if (daysAgo > 30) return { value: `> 30 dní`, bg: "bg-amber-50 text-amber-700 border-amber-200" };
    return { value: "Ano", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  })();

  return (
    <button
      onClick={onOpen}
      className={`w-full text-left bg-white rounded-xl border border-slate-200 ${accentBorder} shadow-sm overflow-hidden hover:shadow-md hover:bg-slate-50 transition-all cursor-pointer`}
    >
      <div className="px-4 py-3 sm:py-3.5">
        {/* Řádek 1 - jméno + status + balíček pill */}
        <div className="flex items-start gap-3 mb-2 sm:mb-2.5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-base sm:text-lg font-bold text-slate-900 demo-sensitive">{c.klient || "-"}</div>
              {statusBadge && (
                <div className={`text-xs px-2 py-0.5 rounded-full border font-semibold inline-flex items-center gap-1 ${statusBadge.bg}`}>
                  {StatusIcon && <StatusIcon className="w-3 h-3" />}
                  {statusBadge.text}
                </div>
              )}
              {c.balicek && (
                <div className="text-xs px-2 py-0.5 rounded-full border border-plum-200 bg-plum-50 text-plum-700 font-semibold inline-flex items-center gap-1">
                  <Package className="w-3 h-3" /> {c.balicek}
                </div>
              )}
              {c._isCizi && (
                <div className="text-[11px] px-2 py-0.5 rounded-full border border-amber-300 bg-amber-100 text-amber-800 font-bold uppercase tracking-wider" title={`Cizí klient - oficiální coach: ${c.trener || "?"}`}>
                  cizí, {c.trener}
                </div>
              )}
            </div>
          </div>
          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0 -rotate-90 mt-1" />
        </div>

        {/* Řádek 2 - pill boxy s klíčovými metrikami */}
        <div className="flex items-stretch gap-1.5 flex-wrap">
          {spolupracePill && (
            <div className={`flex items-baseline gap-1.5 px-2.5 py-1 rounded-xl border ${spolupracePill.bg}`} title="Celková délka spolupráce">
              <span className="text-[11px] uppercase tracking-wider font-bold opacity-70">Spolupráce</span>
              <span className="text-sm font-bold">{spolupracePill.value}</span>
            </div>
          )}
          {stavTreninkuPill && (
            <div className={`flex items-baseline gap-1.5 px-2.5 py-1 rounded-xl border ${stavTreninkuPill.bg}`} title={`Tréninky k odchození (vč. bonusových). Záporné = klient si odchodil dopředu.`}>
              <span className="text-[11px] uppercase tracking-wider font-bold opacity-70">K odchození</span>
              <span className="text-sm font-extrabold tabular-nums">{stavTreninkuPill.value}</span>
            </div>
          )}
          <div className={`flex items-baseline gap-1.5 px-2.5 py-1 rounded-xl border ${tentoMesicPill.bg}`} title={`Tréninky odchozené tento měsíc / cílový počet pro ${packageRules.label} (minimum ${packageRules.monthlyMin})`}>
            <span className="text-[11px] uppercase tracking-wider font-bold opacity-70">Tento měsíc</span>
            <span className="text-sm font-extrabold tabular-nums">{tentoMesicPill.value}</span>
          </div>
          <div className="flex items-baseline gap-1.5 px-2.5 py-1 rounded-xl border bg-slate-50 text-slate-700 border-slate-200" title="Datum posledního tréninku">
            <span className="text-[11px] uppercase tracking-wider font-bold opacity-70">Poslední</span>
            <span className="text-sm font-bold">{lastTrainingLabel}</span>
          </div>
          <div className={`flex items-baseline gap-1.5 px-2.5 py-1 rounded-xl border ${fakturaPill.bg}`} title="Stav poslední faktury">
            <span className="text-[11px] uppercase tracking-wider font-bold opacity-70">Poslední faktura</span>
            <span className="text-sm font-bold">{fakturaPill.value}</span>
          </div>
          <div className={`flex items-baseline gap-1.5 px-2.5 py-1 rounded-xl border ${appLoginPill.bg}`} title="Posledně přihlášený do klientské aplikace (SMS verify)">
            <span className="text-[11px] uppercase tracking-wider font-bold opacity-70">V aplikaci</span>
            <span className="text-sm font-bold">{appLoginPill.value}</span>
          </div>
        </div>
      </div>
    </button>
  );
}



